import { computed } from "vue"
import timeHelpers from "@/helpers/time_helpers"
import vueI18n from "@/plugins/i18n"
import { useQuizzesAttemptSummary } from "@/composables/useQuizzesAttemptSummary"


export function useQuizSummary({ quiz, quizzesAttemptSummary, session }) {
  const instructions = computed(() => {
    const formattedDuration = timeHelpers.formatDuration(
      (quizzesAttemptSummary?.time_limit_with_accommodation || quiz.time_limit) * 1000
    )

    // Temporary fix for quizzesAttemptSummary while useQuizSummary is not reactive
    const reactiveQuizzesAttemptSummary = computed(() => quizzesAttemptSummary)

    const { timeAccommodation, remainingTimeFormatted }  = useQuizzesAttemptSummary(reactiveQuizzesAttemptSummary)

    const durationLabel = quiz.time_limit ?
      vueI18n.t("live.timer", { timer: formattedDuration }) :
      vueI18n.t("live.examOnboarding.grading.noTimer")

    return [
      {
        type: "questionsCount",
        icon: "mdi-format-list-checks",
        group: "subject",
        label: vueI18n.t(
          "live.examOnboarding.grading.questions",
          { questions: quiz.questions_count }
        ),
        visible: true,
      },
      {
        type: "defaultDuration",
        icon: "mdi-timer-outline",
        label: durationLabel,
        visible: quiz.quiz_type === "exam",
      },
      {
        type: "questionsNavigation",
        icon: "mdi-undo",
        group: "quizRules",
        label: quiz.allow_questions_navigation ?
          vueI18n.t("live.examOnboarding.grading.navigation") :
          vueI18n.t("live.examOnboarding.grading.noNavigation"),
        visible: quiz.quiz_type === "exam",
      },
      {
        type: "quizOpeningTime",
        icon: "mdi-clock-outline",
        group: "time",
        label: `${vueI18n.t("live.startTime")} ${formatDate(quizzesAttemptSummary?.start_at)}`,
        visible: quizzesAttemptSummary?.start_at,
      },
      {
        type: "remainingTime",
        icon: "mdi-clock-outline",
        group: "time",
        label: `${vueI18n.t("live.remainingTime")} ${remainingTimeFormatted.value}`,
        visible: quizzesAttemptSummary?.end_at_with_accommodation && quizzesAttemptSummary?.student_started_at,
      },
      {
        type: "noDeadline",
        icon: "mdi-clock-outline",
        group: "time",
        label: vueI18n.t("live.examOnboarding.grading.noDeadline"),
        visible: !quizzesAttemptSummary?.submission_deadline_at,
      },
      {
        type: "studentDeadline",
        icon: "mdi-clock-end",
        group: "time",
        label: `${vueI18n.t("live.stopTime")} ${formatDate(quizzesAttemptSummary?.submission_deadline_at)}`,
        visible: quizzesAttemptSummary?.submission_deadline_at,
      },
      {
        type: "sessionOpeningTime",
        icon: "mdi-clock-outline",
        group: "time",
        label: `${vueI18n.t("live.startTime")} ${formatDate(session?.start_at)}`,
        visible: session?.start_at && !quizzesAttemptSummary,
      },
      {
        type: "quizClosingTime",
        icon: "mdi-clock-end",
        group: "time",
        label: `${vueI18n.t("live.stopTime")} ${formatDate(quizzesAttemptSummary?.end_at_with_accommodation)}`,
        visible: quizzesAttemptSummary?.end_at_with_accommodation,
      },
      {
        type: "sessionClosingTime",
        icon: "mdi-clock-end",
        group: "time",
        label: `${vueI18n.t("live.stopTime")} ${formatDate(session?.end_at)}`,
        visible: session?.end_at && !quizzesAttemptSummary,
      },
      {
        type: "timeAccommodation",
        icon: "mdi-timer-plus-outline",
        group: "time",
        label: vueI18n.t(
          "live.examOnboarding.grading.timeAccommodation",
          { name: timeAccommodation.value.name }
        ),
        visible: quiz.time_accommodation && quizzesAttemptSummary,
      },
      {
        type: "videoProctoring",
        icon: "mdi-video-outline",
        group: "quizRules",
        label: vueI18n.t("live.examOnboarding.grading.videoProctoring.rule"),
        visible: quiz.video_proctoring,
      },
      {
        type: "screenRecording",
        icon: "mdi-monitor-screenshot",
        group: "quizRules",
        label: vueI18n.t("live.examOnboarding.grading.screenRecording.rule"),
        visible: quiz.screen_recording,
      },
      {
        type: "requireFullscreen",
        icon: "mdi-fullscreen",
        group: "quizRules",
        label: vueI18n.t("live.examOnboarding.grading.requireFullscreen.rule"),
        visible: quiz.require_fullscreen,
      },
    ].filter(e => e.visible)
  })

  const gradingStudentInfo = computed(() => {
    const grading = quiz.grading_description || ""
    return (grading.length === 0 || grading === "<p></p>") ? vueI18n.t("quiz.gradeRules") : grading
  })

  const examRulesStudentInfo = computed(() => {
    const examRules = quiz.instructions || ""
    return (examRules.length === 0 || examRules === "<p></p>") ? "" : examRules
  })

  const onboardingFirstPageInstructions = computed(() => {
    const filter = (e) => [
      "questionsCount",
      "defaultDuration",
      "quizOpeningTime",
      "sessionOpeningTime",
      "quizClosingTime",
      "sessionClosingTime",
      "timeAccommodation",
      "questionsNavigation",
    ].includes(e.type)
    return instructions.value.filter(filter)
  })

  const inExamInstructions = computed(() => {
    const filter = (e) => [
      "questionsCount",
      "remainingTime",
      "timeAccommodation",
      "videoProctoring",
      "screenRecording",
      "requireFullscreen",
      "studentDeadline",
      "noDeadline",
      "questionsNavigation",
    ].includes(e.type)
    return instructions.value.filter(filter)
  })

  function formatDate(date) {
    return timeHelpers.formatDateTime(date) + " - " + Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  return {
    instructions,
    gradingStudentInfo,
    examRulesStudentInfo,
    onboardingFirstPageInstructions,
    inExamInstructions,
  }
}
